// Toast notification service
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

type ToastCallback = (toasts: Toast[]) => void;

class ToastService {
  private listeners: Set<ToastCallback> = new Set();
  private toasts: Toast[] = [];

  subscribe(callback: ToastCallback): () => void {
    this.listeners.add(callback);
    callback(this.toasts);
    return () => this.listeners.delete(callback);
  }

  private notify() {
    this.listeners.forEach((callback) => callback([...this.toasts]));
  }

  private addToast(type: ToastType, message: string, duration = 5000) {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: Toast = { id, type, message, duration };
    this.toasts.push(toast);
    this.notify();

    if (duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }

    return id;
  }

  success(message: string, duration?: number) {
    return this.addToast('success', message, duration);
  }

  error(message: string, duration?: number) {
    return this.addToast('error', message, duration ?? 8000);
  }

  warning(message: string, duration?: number) {
    return this.addToast('warning', message, duration);
  }

  info(message: string, duration?: number) {
    return this.addToast('info', message, duration);
  }

  remove(id: string) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.notify();
  }

  clear() {
    this.toasts = [];
    this.notify();
  }
}

export const toastService = new ToastService();
